// @ts-strict-ignore
import { createSelector } from 'reselect';

import { ActionType } from 'flow/store/engine/constants';
import {
  isActionOfType,
  isCustomListAction,
} from 'flow/service/actions';
import type { WorkflowInfo, WorkflowType } from '~/dashboard/views/workflows/store/engine/types/props';

import type { State } from '~/store';
import type { DataRoute, Tag, Threshold } from 'flow/store/engine/types';

import type { DerivedNodeConnectorState } from '../types';
import {
  selectPreviewOutcomeAction, selectPreviewWorkflowRoutingAction,
} from './actions';
import { reversedObjectKeyVal } from '~/entities/evaluations/TagRuleModal/utils';
import { populateThresholdData } from 'flow/service/thresholds';

export function selectWorkflowData(state: State) {
  return state.workflowEditor.workflowData;
}

export function selectWorkflowProps(state: State) {
  return state.workflowEditor?.workflowProps;
}

export function selectFeatureFlagsForStore(state: State) {
  return state.workflowEditor?.featureFlagsForStore;
}

export function selectCustomTransactionAttributesEnabled(state: State) {
  return state.workflowEditor?.featureFlagsForStore?.customTransactionAttributes ?? false;
}

export function selectWorkflow(state: State) {
  return state.workflowEditor?.workflowProps?.workflow;
}
export function selectWorkflowOperationDescs(state: State) {
  return state.workflowEditor?.workflowOperationDescriptions;
}

export function selectActions(state: State) {
  return state.workflowEditor.workflowData?.actions;
}

export function selectDataRoutes(state: State) {
  return state.workflowEditor.workflowData?.dataRoutes;
}

export function selectPublicVersion(state: State) {
  return state.workflowEditor.workflowData?.publicVersion;
}

export function selectWorkflowType(state: State) {
  return state.workflowEditor.workflowProps?.workflow?.type;
}

export function selectTags(state: State) {
  return state?.workflowEditor?.workflowProps?.tags;
}

export function selectTagByName(tagName: string) {
  return function selector(state: State) {
    const tags = selectTags(state);
    return tags.find(tag => tag.tag_name === tagName);
  };
}

export const selectTagsByName = createSelector(
  [
    selectTags,
  ],
  (tags): Record<Tag['tag_name'], Tag> => (
    Object.fromEntries((tags ?? []).map(tag => [tag.tag_name, tag]))
  ),
);

export const selectUniqueTagNames = createSelector(
  [
    selectTagsByName,
  ],
  (tagsByName): Set<Tag['tag_name']> => new Set(Object.keys(tagsByName)),
);


export function selectOutcomes(state: State) {
  return state?.workflowEditor?.workflowProps?.outcomes;
}

export function selectDataRoutesByIds(datarouteIds: DataRoute['publicId'][]) {
  return function selector(state: State): DataRoute[] {
    const dataroutes = selectDataRoutes(state);
    return datarouteIds.map(id => dataroutes[id]);
  };
}

export function selectThresholds(state: State) {
  return state?.workflowEditor?.workflowData?.thresholds;
}

export function selectThresholdById(thresholdId: Threshold['publicId']) {
  return function selector(state: State) {
    const thresholds = selectThresholds(state);
    return thresholds[thresholdId];
  };
}

export function selectThresholdsByIds(thresholdIds: Threshold['publicId'][]) {
  return function selector(state: State): Threshold[] {
    const thresholds = selectThresholds(state);
    return thresholdIds.map(id => thresholds[id]);
  };
}

export function selectPopulatedThresholdById(thresholdId: string) {
  return function selector(state: State) {
    const threshold = selectThresholdById(thresholdId)(state);
    const workflowData = selectWorkflowData(state);
    const workflowProps = selectWorkflowProps(state);

    return threshold
      ? populateThresholdData({
        actions: workflowData.actions,
        reasonCodeGroups: workflowProps.reasonCodeGroups,
        services: workflowProps.services,
        threshold,
      })
      : null;
  };
}

export function selectActionByPublicId(publicId: string) {
  return function selector(state: State) {
    return state.workflowEditor.workflowData?.actions[publicId] ?? null;
  };
}

export function selectAllWorkflows(): (state: State) => WorkflowInfo[];
export function selectAllWorkflows(
  opts: { byApplicationId: boolean }
): (state: State) => Record<string, WorkflowInfo>;
export function selectAllWorkflows(
  opts: { byToken: boolean }
): (state: State) => Record<string, WorkflowInfo>;
export function selectAllWorkflows(opts?: { byApplicationId: boolean; byToken: boolean }) {
  return function selector(state: State) {
    const { byApplicationId, byToken } = opts ?? { byApplicationId: false, byToken: false };
    const { allWorkflows } = state.workflowEditor.workflowProps;

    if (byApplicationId) {
      return allWorkflows.reduce((all, workflow) => ({
        ...all,
        [workflow.application_id]: workflow,
      }), {} as Record<string, WorkflowInfo>);
    }

    if (byToken) {
      return allWorkflows.reduce((all, workflow) => ({
        ...all,
        [workflow.application_token]: workflow,
      }), {} as Record<string, WorkflowInfo>);
    }

    return allWorkflows;
  };
}

export function selectImportableOutcomeReasons(state: State) {
  return state.workflowEditor.workflowProps?.tags
    .filter(tag => tag.outcomes?.length > 0);
}

export function selectImportableOutputAttributes(state: State) {
  return state.workflowEditor.workflowProps?.importableAttributes.output_attributes;
}

export function selectImportableMatrices(state: State) {
  return state.workflowEditor.workflowProps?.importableAttributes.matrix_models;
}

export function selectLists(state: State) {
  return state.workflowEditor.workflowProps?.lists;
}

export function selectCustomLists(state: State) {
  return state.workflowEditor.workflowProps?.customLists;
}

export function selectCustomListByActionPublicId(actionPublicId: string) {
  return createSelector(
    [
      (state: State) => selectActions(state),
      (state: State) => selectCustomLists(state),
    ],
    (actions, customLists) => {
      const action = actions[actionPublicId];

      const defaultReturn = {
        currentVersion: null,
        activeVersion: null,
        customListName: null,
      };

      if (!isCustomListAction(action)) {
        return defaultReturn;
      }

      const customList = customLists.find(cl => cl.name === action.service.name);
      if (!customList) return defaultReturn;

      // Get current major version from action options
      const currentMajorVersion = action.options?.custom_list_major_version?.value
        ? Number(action.options.custom_list_major_version.value)
        : null;

      // Find the version object that matches the current major version
      const currentVersion = currentMajorVersion
        ? customList.versions.find(v => v.major_version === currentMajorVersion)
        : null;

      // Find the version object that matches the active major version
      const activeVersion = customList.versions.find(
        v => v.major_version === customList.active_major_version,
      );

      return {
        currentVersion,
        activeVersion,
        customListName: customList.name,
      };
    });
}

export function selectServices(state: State) {
  return state.workflowEditor.workflowProps?.services;
}

export function selectIsOnboardingWorkflow(state: State) {
  const onboardingTypes: WorkflowType[] = ['business', 'person'];
  return onboardingTypes.includes(state.workflowEditor.workflowProps?.workflow?.type);
}

export function selectRecurringWorkflows(state: State) {
  return state.workflowEditor.workflowProps?.recurringWorkflows;
}

export function selectPublishedAttributes(state: State) {
  return state.workflowEditor.workflowProps?.publishedAttributes;
}

export function selectReasonCodeGroups(state: State) {
  return state.workflowEditor.workflowProps?.reasonCodeGroups;
}

export function selectReasonCodes(state: State) {
  return state.workflowEditor.workflowProps?.reasonCodes;
}

export function selectWorkflowIsSnippet(state: State) {
  return !!state.workflowEditor.workflowProps?.workflow?.is_snippet;
}

export function selectWorkflowBuilderUIStateForEdge(state: State) {
  const {
    canvasProperties: { isMoving },
    focusedNodeId,
    activeNodeEditorTab,
    hoveredNodeId,
  } = state.workflowEditor.ui;

  return {
    isMoving,
    focusedNodeId,
    activeNodeEditorTab,
    hoveredNodeId,
  };
}

export function selectActiveNodeEditorTab(state: State) {
  return state.workflowEditor.ui.activeNodeEditorTab;
}

export function selectIsTagSwapMode(state: State) {
  return state.workflowEditor.ui.isTagSwapMode;
}

export function selectFocusedAction(state: State) {
  const {
    workflowEditor: {
      workflowData,
      ui: {
        focusedNodeId,
      },
    },
  } = state;

  if (!workflowData || !focusedNodeId) {
    return null;
  }
  return workflowData.actions[focusedNodeId];
}

export function selectHighlightedAction(state: State) {
  const {
    workflowEditor: {
      workflowData,
      ui: {
        highlightedNodeId,
      },
    },
  } = state;

  if (!workflowData || !highlightedNodeId) {
    return null;
  }
  return workflowData.actions[highlightedNodeId];
}


/**
 * Computes the intent of the connector
 * this is written in a standalone memoized selector so that `selectNodeConnectorState does not
 * execute every time a node is hovered
 */
const selectConnectorIntent = createSelector(
  [
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.ui.hoveredNodeId,
  ],
  (focusedNodeId, hoveredNodeId) => {
    /**
     * intent refers to _what_ the connector is trying to do
     * intent states:
     * 1 initial - no node focused => add
     * 2 focused - new or existing action => link
     * 3 (exception to 2) hovered over focused => add
     */
    const intent = !focusedNodeId || focusedNodeId === hoveredNodeId ? 'add' : 'link';
    return intent;
  },
);
export const selectNodeConnectorState = createSelector(
  [
    (state: State) => state.workflowEditor.ui.connector,
    selectConnectorIntent,
  ],
  (connectorState, intent): DerivedNodeConnectorState => {
    const { canCreateItem, interactionState, ownerNodeId } = connectorState;
    return {
      intent,
      interactionState,
      ownerNodeId,
      canCreateItem,
    };
  },
);

export function selectIsComplexOutcomeIslandOpen(state: State) {
  return isActionOfType(selectFocusedAction(state), ActionType.OUTCOME);
}

export function selectIsComplexWorkflowRoutingIslandOpen(state: State) {
  return isActionOfType(selectFocusedAction(state), ActionType.WORKFLOW);
}

export function selectIsNodeEditorDrawerOpen(state: State) {
  return Boolean(selectActiveNodeEditorTab(state));
}


export function selectIsEditingLinkedChildAction(state: State): boolean {
  const focusedAction = selectFocusedAction(state);
  return focusedAction?.type === ActionType.PREVIEW;
}

export function selectLinkedChildActionState(state: State) {
  return state.workflowEditor.linkedChildAction;
}

export function selectCanSaveWorkflow(state: State) {
  if (
    selectIsEditingLinkedChildAction(state)
    || selectPreviewOutcomeAction(state)
    || selectPreviewWorkflowRoutingAction(state)
    // ... add future conditions the prevent saving here
  ) {
    return false;
  }


  return true;
}


export function selectCanPerformTopLevelInteraction(state: State) {
  // component state that can prevent top-level interactions
  const hasComponentStatePreventingInteraction = (
    state.workflowEditor.ui?.components.services.serviceRoutingCategoryNameInEditing
    || state.workflowEditor.ui?.components.services.serviceActionOptionPublicIdInEditing
    // || ... add more component state checks here
  );

  return !hasComponentStatePreventingInteraction && selectCanSaveWorkflow(state);
}


export function selectLegacyMappings(state: State) {
  return state?.workflowEditor?.legacyMappings;
}

export const selectReversedLegacyActionMappings = createSelector(
  [selectLegacyMappings],
  legacyMappings => reversedObjectKeyVal(legacyMappings.actions || {}),
);

export const selectReversedLegacyThresholdMappings = createSelector(
  [selectLegacyMappings],
  legacyMappings => reversedObjectKeyVal(legacyMappings.thresholds || {}),
);

export function selectWorkflowFailureHandlingMode(state: State) {
  return state?.workflowEditor?.workflowData?.failureHandlingMode;
}

export function selectWorkflowSupportsCopyPaste(state: State) {
  const workflowType = selectWorkflowType(state);
  // router (legacy router) workflows do not support copy/paste
  return workflowType !== 'router';
}
